import dayjs from 'dayjs'
import isToday from 'dayjs/plugin/isToday'
import isTomorrow from 'dayjs/plugin/isTomorrow'
import cloneDeep from 'lodash/cloneDeep'
import groupBy from 'lodash/groupBy'
import forIn from 'lodash/forIn'

import type { CategorySchedules, FootballSchedule, MatchData, MatchItem, PlaySportItem } from '~/utils/interface'
import { LABEL_ALL_GAME, SLUG_SPORTS, DATE_TIME_FORMAT, HALF_DAY } from '~/constants'

import { useGames } from '~/composables/games/use-games'
import { useApi } from '~/composables/common/use-api'
import { useNavigate } from '~/composables/common/use-navigate'
import { ResponseStatus } from '~composables/common/interface'
import { CategoriesSlug, SLUG_ALL_SPORTS, SPORTS_LIMIT, SportsTabs } from '~composables/sports/interface'
import type { CategoryItem } from '~composables/sports/interface'

import { IFRAME_GAME_URLS } from '~configs/page-url'
import { MEDIA_API } from '~/configs/api'
import { HttpRequestMethod } from '~/configs/enums'
export function useSports() {
  type ScheduleApiItem = MatchItem & { league_name: string; league_image?: string }
  dayjs.extend(isToday)
  dayjs.extend(isTomorrow)

  const { playGame, openModalMaintainAthenaGames, isMaintainAthenaGames } = useGames()
  const { navigate } = useNavigate()
  const { isMobile } = useDevice()
  const categorySchedules = reactive<CategorySchedules[]>([])
  const scheduleSports = reactive<FootballSchedule[]>([])
  let originScheduleSports: FootballSchedule[] = []
  const schedulesState = useState<FootballSchedule[]>('scheduleSportsData', () => [])
  const route = useRoute()
  const menuSwiperInit = ref()
  const totalSchedule = ref(0)
  const hasAccountTrial = ref(false)
  const leagueName = ref(route.query?.leagues?.toString() ?? '')
  const isLoadingSchedule = ref(false)
  const tabSelected = ref(SportsTabs.SPORT)
  const { request } = useApi()
  const runtimeConfig = useRuntimeConfig()
  const IMG_BASE = runtimeConfig.public.IMG_HOT_MATCH_BASE
  // const hotMatchesApi = runtimeConfig.public.HOT_MATCH_API
  const offSetSchedule = 0
  const numberPagedSports = 1
  let numberPages = numberPagedSports
  const isDropDownNotification = ref(false)
  const currentCategorySelected = ref(CategoriesSlug.ALL)
  function toggleDropDownNotification(): void {
    isDropDownNotification.value = !isDropDownNotification.value
  }
  function playSport(item: PlaySportItem): void {
    if (item.isMaintain || item.isComingSoon || !item) {
      return
    }
    if (item?.link) {
      void navigate(item.link, item.loginRequired, item.newTab)
    } else {
      void playGame(item)
    }
  }
  function openKSportGame(hotmatchItem: MatchData | undefined): void {
    if (!hotmatchItem || hotmatchItem?.isDisable) {
      return
    }
    const playItem = {
      link: `${IFRAME_GAME_URLS.K_SPORTS}?eventId=${hotmatchItem?.match_id ?? ''}&leagueId=${
        hotmatchItem.league_id ?? ''
      }`
    }
    playSport(playItem)
  }
  const playASportGame = async (hotmatch: MatchData): Promise<void> => {
    if (isMaintainAthenaGames(SLUG_SPORTS.A_SPORTS)) {
      openModalMaintainAthenaGames()
      return
    }
    if (isMobile) {
      const leagueId = hotmatch?.athena_league_id ?? '43'
      const urlApi = `${IFRAME_GAME_URLS.A_SPORTS}?gameName=the-thao&matchId=${
        hotmatch?.athena_match_id ?? ''
      }&leagueId=${leagueId}&isMobile=true`
      void navigate(urlApi, false, true)
    } else {
      void navigate(`${IFRAME_GAME_URLS.A_SPORTS}?mid=${hotmatch?.athena_match_id ?? ''}`, false, false)
    }
  }

  function clickSportsTab(tab: SportsTabs): void {
    tabSelected.value = tab
  }
  function onSwiperLeagues(swiper: object): void {
    menuSwiperInit.value = swiper
    const index = categorySchedules.findIndex(item => item.slug === leagueName.value)
    menuSwiperInit.value?.slideTo(index > 0 ? index - 1 : index)
  }

  const setScheduleMatches = async (): Promise<FootballSchedule[]> => {
    isLoadingSchedule.value = true
    try {
      const { data, status } = await request(MEDIA_API.FOOTBALL_SCHEDULE_FIXTURE, {
        method: HttpRequestMethod.GET
      })
      if (status === ResponseStatus.OK) {
        const dataConvert: ScheduleApiItem[] = []
        for (const key in data) {
          if (Object.prototype.hasOwnProperty.call(data, key)) {
            dataConvert.push(...(data[key] as ScheduleApiItem[]))
          }
        }
        const listHotmach: FootballSchedule[] = []
        const dataGroupHotmach = groupBy(dataConvert, 'league_name')
        forIn(dataGroupHotmach, (value, key) => {
          if (value[0]) {
            const item = {
              leagueName: key,
              logo: value[0]?.league_image,
              matches: value
            }
            listHotmach.push(item)
          }
        })
        schedulesState.value = listHotmach
        applySchedules(listHotmach)
        return listHotmach
      }
      return []
    } catch {
      return []
    } finally {
      isLoadingSchedule.value = false
    }
  }

  function applySchedules(schedules: FootballSchedule[]): void {
    setCategorySchedules(schedules)
    setSchedulesLeague(schedules)
    totalSchedule.value = schedules.length
  }

  if (schedulesState.value && schedulesState.value.length) {
    applySchedules(schedulesState.value)
  }

  function setCategorySchedules(schedules: FootballSchedule[]): void {
    const leagues = schedules.map(it => {
      return {
        slug: it?.leagueName?.replaceAll(' ', '-').toLowerCase() || '',
        thumb: it?.logo ?? '',
        name: it?.leagueName?.toLowerCase() || ''
      }
    })
    categorySchedules.splice(
      0,
      categorySchedules.length,
      ...[
        {
          thumb: '/assets/images/sports/all-match.png',
          name: LABEL_ALL_GAME,
          slug: SLUG_ALL_SPORTS
        },
        ...leagues
      ]
    )
  }
  function filterSchedulesData(matchesData: FootballSchedule[]): FootballSchedule[] {
    matchesData.forEach(match => {
      const matchingItem = findMatchingItem(match)
      if (matchingItem) {
        match.thumb = matchingItem.thumb
        match.slug = matchingItem.slug
      }
    })
    return matchesData.filter(match => doesMatchBelongToLeague(match))
  }

  function findMatchingItem(match: FootballSchedule): CategorySchedules | undefined {
    return categorySchedules.find(item => match?.leagueName?.toLowerCase() === item.name.toLowerCase())
  }

  function doesMatchBelongToLeague(match: FootballSchedule): boolean {
    return categorySchedules.some(item => match?.leagueName?.toLowerCase() === item.name.toLowerCase())
  }

  function setSchedulesLeague(matchesData: FootballSchedule[]): void {
    originScheduleSports = filterSchedulesData(matchesData)
    filterSchedules()
  }

  function changeLeagueName(slug: string, index: number): void {
    numberPages = numberPagedSports
    leagueName.value = slug
    const step = isMobile ? index : index - 1
    menuSwiperInit.value.slideTo(index > 0 ? step : index)
    filterSchedules()
  }

  function loadMoreSchedules(): void {
    numberPages += numberPagedSports
    filterSchedules()
  }

  function filterSchedules(): void {
    scheduleSports.splice(0, scheduleSports.length)
    const itemPerPage = numberPages * SPORTS_LIMIT
    if (leagueName.value === SLUG_ALL_SPORTS || !leagueName.value) {
      // if (isMobile) {
      //   const schedulesFilter: FootballSchedule[] = originScheduleSports.map((schedule: FootballSchedule) => {
      //     const matches = filterMatchesByCategory(schedule.matches, currentCategorySelected.value)
      //     return {
      //       ...schedule,
      //       matches
      //     }
      //   })
      //   scheduleSports.push(...schedulesFilter)
      // } else {
      //   scheduleSports.push(...originScheduleSports.slice(offSetSchedule, itemPerPage))
      // }
      scheduleSports.push(...originScheduleSports.slice(offSetSchedule, itemPerPage))
      removeEmptyMatches()
      return
    }
    const newScheduleSports = filterSchedulesByLeagueSlug(leagueName.value)
    newScheduleSports.forEach((schedule: FootballSchedule) => {
      schedule.matches = filterMatchesByCategory(schedule.matches, currentCategorySelected.value)
    })
    scheduleSports.push(...newScheduleSports.slice(offSetSchedule, itemPerPage))
    removeEmptyMatches()
  }

  function filterSchedulesByLeagueSlug(leagueName: string): FootballSchedule[] {
    return cloneDeep(originScheduleSports).filter(
      (match: FootballSchedule) => match?.slug?.toLowerCase() === leagueName.toLowerCase()
    )
  }
  function removeEmptyMatches(): void {
    for (let i = scheduleSports.length - 1; i >= 0; i--) {
      if (!scheduleSports[i].matches || scheduleSports[i].matches.length === 0) {
        scheduleSports.splice(i, 1)
      }
    }
  }

  function filterMatchesByCategory(matches: MatchItem[], slug: string): MatchItem[] {
    const matchesTemp: MatchItem[] = []

    const categoryConditions: Record<string, (match: MatchItem) => boolean> = {
      [CategoriesSlug.TODAY]: match => match.live ?? dayjs(match.text_time).isToday(),
      [CategoriesSlug.TOMORROW]: match => dayjs(match.text_time).isTomorrow()
    }

    const condition = categoryConditions[slug] || (() => true)
    matchesTemp.push(...matches.filter(condition))

    return matchesTemp
  }

  function changeCategory(category: CategoryItem): void {
    currentCategorySelected.value = category.slug
    filterSchedules()
  }
  function checkTimeUpcoming(time: string): boolean {
    const nowDate = dayjs().format(DATE_TIME_FORMAT.FULL_DATE_FORMAT_SCHEDULE)
    if (dayjs(time).unix() - dayjs(nowDate).unix() > 0 && dayjs(time).unix() - dayjs(nowDate).unix() <= HALF_DAY) {
      return true
    }
    return false
  }
  return {
    hasAccountTrial,
    playSport,
    tabSelected,
    clickSportsTab,
    categorySchedules,
    currentCategorySelected,
    setScheduleMatches,
    scheduleSports,
    playASportGame,
    leagueName,
    changeLeagueName,
    onSwiperLeagues,
    totalSchedule,
    isLoadingSchedule,
    loadMoreSchedules,
    openKSportGame,
    IMG_BASE,
    toggleDropDownNotification,
    isDropDownNotification,
    changeCategory,
    checkTimeUpcoming
  }
}
